
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { StatsResult } from '@/hooks/use-studio-stats';
import { 
  ResponsiveContainer, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from 'recharts';

interface CustomerBehaviorProps {
  stats: StatsResult;
  isLoading: boolean;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const CustomerBehavior: React.FC<CustomerBehaviorProps> = ({ stats, isLoading }) => {
  // Time of day preference
  const timeData = stats.timeDistribution.map(item => ({
    hour: `${item.hour}:00`,
    count: item.count
  }));
  
  // Generate mock data for recurring vs new customers
  const totalCustomers = stats.customers;
  const recurringCustomers = Math.floor(totalCustomers * 0.65);
  const newCustomers = totalCustomers - recurringCustomers;
  
  const customerTypeData = [
    { name: "Stammkunden", value: recurringCustomers },
    { name: "Neukunden", value: newCustomers }
  ];
  
  return (
    <div className="space-y-6">
      {/* Customer Type Distribution */}
      <Card>
        <CardHeader>
          <CardTitle>Kundenstruktur</CardTitle>
          <CardDescription>
            Verteilung zwischen Stamm- und Neukunden
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center items-center h-60">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-chrona-accent"></div>
            </div>
          ) : stats.customers === 0 ? (
            <div className="flex justify-center items-center h-60">
              <p className="text-muted-foreground">Keine Daten verfügbar</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Pie Chart */}
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={customerTypeData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                      nameKey="name"
                      label={({name, value, percent}) => `${name}: ${Math.round(percent*100)}%`}
                    >
                      {customerTypeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [`${value} Kunden`, 'Anzahl']} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              
              {/* Stats */}
              <div className="flex flex-col justify-center space-y-6">
                <div>
                  <h3 className="text-lg font-medium">Kundenbindungsrate</h3>
                  <p className="text-3xl font-bold text-chrona-accent">
                    {Math.round((recurringCustomers / totalCustomers) * 100)}%
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Anteil der wiederkehrenden Kunden
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-medium">Durchschnittliche Buchungen pro Kunde</h3>
                  <p className="text-3xl font-bold text-chrona-accent">
                    {(stats.appointments / totalCustomers).toFixed(1)}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Buchungen im Durchschnitt pro Kunde
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Time Preference */}
      <Card>
        <CardHeader>
          <CardTitle>Beliebteste Buchungszeiten</CardTitle>
          <CardDescription>
            Verteilung der Buchungen nach Tageszeit
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center items-center h-60">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-chrona-accent"></div>
            </div>
          ) : timeData.length === 0 ? (
            <div className="flex justify-center items-center h-60">
              <p className="text-muted-foreground">Keine Daten verfügbar</p>
            </div>
          ) : (
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={timeData}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hour" />
                  <YAxis />
                  <Tooltip formatter={(value) => [`${value} Buchungen`, 'Anzahl']} />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="count" 
                    stroke="#2C6E49" 
                    name="Buchungen"
                    strokeWidth={2}
                    activeDot={{ r: 8 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Day Preference */}
      <Card>
        <CardHeader>
          <CardTitle>Beliebteste Buchungstage</CardTitle>
          <CardDescription>
            Verteilung der Buchungen nach Wochentagen
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center items-center h-60">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-chrona-accent"></div>
            </div>
          ) : stats.weekdayDistribution.length === 0 ? (
            <div className="flex justify-center items-center h-60">
              <p className="text-muted-foreground">Keine Daten verfügbar</p>
            </div>
          ) : (
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={stats.weekdayDistribution}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <Tooltip formatter={(value) => [`${value} Buchungen`, 'Anzahl']} />
                  <Legend />
                  <Bar 
                    dataKey="count" 
                    fill="#4C956C" 
                    name="Buchungen"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Key Insights Card */}
      <Card className="bg-card/50 backdrop-blur-sm border-chrona-accent/30">
        <CardHeader>
          <CardTitle>Kundenverhalten - Insights</CardTitle>
          <CardDescription>
            Wichtige Erkenntnisse aus den Kundendaten
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="space-y-3">
            <li className="flex items-start gap-2">
              <div className="rounded-full bg-chrona-accent h-2 w-2 mt-2" />
              <span>
                <strong>Beste Buchungszeit:</strong> {timeData.sort((a, b) => b.count - a.count)[0]?.hour || "N/A"} 
                - Erwägen Sie, zu dieser Zeit zusätzliches Personal einzusetzen.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <div className="rounded-full bg-chrona-accent h-2 w-2 mt-2" />
              <span>
                <strong>Beliebtester Tag:</strong> {stats.weekdayDistribution.sort((a, b) => b.count - a.count)[0]?.day || "N/A"}
                - An diesem Tag haben Sie die höchste Nachfrage.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <div className="rounded-full bg-chrona-accent h-2 w-2 mt-2" />
              <span>
                <strong>Kundenbindung:</strong> {Math.round((recurringCustomers / totalCustomers) * 100)}% Ihrer Kunden kommen wieder.
                {(recurringCustomers / totalCustomers) < 0.6 
                  ? " Verbessern Sie die Kundenbindung durch Folgeangebote."
                  : " Hervorragende Kundenbindung!"}
              </span>
            </li>
            <li className="flex items-start gap-2">
              <div className="rounded-full bg-chrona-accent h-2 w-2 mt-2" />
              <span>
                <strong>Empfehlung:</strong> Erwägen Sie Sonderangebote zu weniger stark ausgelasteten Zeiten,
                um die Auslastung gleichmäßiger zu verteilen.
              </span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default CustomerBehavior;
